import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Sparkles, TrendingUp, Lightbulb, AlertCircle } from 'lucide-react';
import type { AIInsights } from '../../services/dashboardService';

interface AIInsightsPanelProps {
  insights: AIInsights;
  generatedAt?: string;
  source?: 'aggregated' | 'realtime';
}

export const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  insights,
  generatedAt,
  source = 'aggregated'
}) => {
  if (!insights || (!insights.summary && insights.trends?.length === 0 && insights.recommendations?.length === 0)) {
    return null;
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  return (
    <Card className="w-full mb-6 border-2 border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2 text-blue-900 dark:text-blue-100">
            <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            AI-Powered Insights
          </CardTitle>
          {generatedAt && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Generated: {formatDate(generatedAt)}
            </span>
          )}
        </div>
        {source === 'realtime' && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            AI insights will be available after daily aggregation
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        {insights.summary && (
          <div className="bg-white dark:bg-glass-surface-dark-mode rounded-lg p-4 shadow-sm border border-blue-100 dark:border-blue-800/50">
            <p className="text-sm text-gray-700 dark:text-text-enterprise-secondary leading-relaxed">
              {insights.summary}
            </p>
          </div>
        )}

        {/* Trends */}
        {insights.trends && insights.trends.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-text-enterprise-primary mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              Key Trends
            </h4>
            <ul className="space-y-2">
              {insights.trends.map((trend, index) => (
                <li
                  key={index}
                  className="text-sm text-gray-700 dark:text-text-enterprise-secondary bg-white dark:bg-glass-surface-dark-mode rounded-lg p-3 shadow-sm border border-blue-100 dark:border-blue-800/50 flex items-start gap-2"
                >
                  <span className="text-blue-600 dark:text-blue-400 font-semibold mt-0.5">•</span>
                  <span>{trend}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendations */}
        {insights.recommendations && insights.recommendations.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-text-enterprise-primary mb-2 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              Recommendations
            </h4>
            <ul className="space-y-2">
              {insights.recommendations.map((recommendation, index) => (
                <li
                  key={index}
                  className="text-sm text-gray-700 dark:text-text-enterprise-secondary bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 shadow-sm border border-amber-200 dark:border-amber-800/50 flex items-start gap-2"
                >
                  <span className="text-amber-600 dark:text-amber-400 font-semibold mt-0.5">→</span>
                  <span>{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

